import { api } from "jiffy-api";
import { redirectTo } from "common/utils/browser";
import { makeFullPath } from "common/utils/request/utils";
import { track } from "jiffy-analytics";

// Constants
const SELECTORS = {
  SAVED_CARTS_CONTAINER: ".js-saved-carts-container",
  SAVED_CART_ITEM_TEMPLATE: ".js-saved-cart-item-template",
  SAVED_CART_ITEM: ".js-saved-cart-item",
  SAVED_CART_NAME: ".js-saved-cart-name",
  SAVED_CART_BADGE: ".js-saved-cart-badge",
  SAME_DAY_BADGE_CONTAINER: ".js-same-day-badge-container",
  ADD_CART_BUTTON: '[data-testid="add-cart-button"]'
};

export default class MastheadCartDropdownItems {
  constructor({
    containerSelector = SELECTORS.SAVED_CARTS_CONTAINER,
    templateSelector = SELECTORS.SAVED_CART_ITEM_TEMPLATE,
    addButtonSelector = SELECTORS.ADD_CART_BUTTON,
    loader = null
  } = {}) {
    this.containerSelector = containerSelector;
    this.templateSelector = templateSelector;
    this.addButtonSelector = addButtonSelector;
    this.loader = loader;
    // Bind the handlers once to maintain consistent references
    this.boundHandleCartItemClick = this.handleCartItemClick.bind(this);
    this.boundHandleAddCartClick = this.handleAddCartClick.bind(this);
  }

  // Getters
  $getSavedCartsContainer = () => document.querySelector(this.containerSelector);

  $getSavedCartItemTemplate = () => document.querySelector(this.templateSelector);

  $getAddCartButton = () => document.querySelector(this.addButtonSelector);

  static $getSavedCartItems() {
    return document.querySelectorAll(SELECTORS.SAVED_CART_ITEM);
  }

  static $getActiveCartItem() {
    return document.querySelector('.js-saved-cart-item[data-active="true"]');
  }

  // Setters for cart item elements
  static setCartItemData($item, cart, isActive) {
    $item.dataset.number = cart.number;
    $item.dataset.active = isActive;
  }

  static setCartItemName($name, cartName, isActive) {
    $name.textContent = cartName;
    if (isActive) {
      $name.classList.add("font-semibold", "text-gray-900");
    } else {
      $name.classList.add("text-gray-600");
    }
  }

  static setCartItemBadge($badge, itemCount) {
    const badgeColor = itemCount > 0 ? "bg-blue-600" : "bg-gray-400";
    const badgeText = itemCount > 0 ? itemCount : "Empty";

    // Remove existing background color classes
    $badge.classList.remove("bg-blue-600", "bg-gray-400");

    // Set new text and color
    $badge.textContent = badgeText;
    $badge.classList.add(badgeColor);
  }

  static setSameDayBadge($container, isSameDayEligible) {
    $container.classList.toggle("hidden", !isSameDayEligible);
  }

  // Actions
  static trackSavedCartsEvent(eventName) {
    track(eventName, { trigger: "view_cart_header", masthead_version: "redesign2025" });
  }

  createCartItemFromTemplate(cart, isActive = false) {
    const $template = this.$getSavedCartItemTemplate();
    if (!$template) return null;

    const $clone = $template.content.cloneNode(true);
    const $item = $clone.querySelector(SELECTORS.SAVED_CART_ITEM);
    const $name = $clone.querySelector(SELECTORS.SAVED_CART_NAME);
    const $badge = $clone.querySelector(SELECTORS.SAVED_CART_BADGE);
    const $sameDayContainer = $clone.querySelector(SELECTORS.SAME_DAY_BADGE_CONTAINER);

    MastheadCartDropdownItems.setCartItemData($item, cart, isActive);
    MastheadCartDropdownItems.setCartItemName($name, cart.cart_name, isActive);
    MastheadCartDropdownItems.setCartItemBadge($badge, cart.items);
    MastheadCartDropdownItems.setSameDayBadge($sameDayContainer, cart.same_day_eligible);

    return $clone;
  }

  async handleCartItemClick(e) {
    e.preventDefault();
    const { number, active } = e.currentTarget.dataset;

    if (active === "true") {
      redirectTo(makeFullPath("/cart"));
      return;
    }

    if (this.loader) {
      await this.loader.executeWithLoader("Switching carts...", async () => {
        await api.account.savedCarts.switch({ number });
        MastheadCartDropdownItems.trackSavedCartsEvent("saved_carts_default_changed");
        redirectTo(makeFullPath("/cart"));
      });
    }
  }

  async handleAddCartClick(e) {
    e.preventDefault();

    MastheadCartDropdownItems.trackSavedCartsEvent("saved_carts_new_cart_started");

    if (this.loader) {
      await this.loader.executeWithLoader("Creating new cart...", async () => {
        const newCart = await api.jiffyshirts.savedCart.create({ saved_carts: { cart_name: "" } });
        if (newCart?.number) {
          redirectTo(makeFullPath("/cart"));
        }
      });
    }
  }

  renderSavedCartsFromData(cartsData, activeCart) {
    const $savedCartsContainer = this.$getSavedCartsContainer();
    if (!$savedCartsContainer) return;

    // Clear existing items
    $savedCartsContainer.innerHTML = "";

    if (cartsData && cartsData.length > 0) {
      cartsData.forEach(cart => {
        const isActive = activeCart && activeCart.number === cart.number;
        const cartElement = this.createCartItemFromTemplate(cart, isActive);
        if (cartElement) {
          $savedCartsContainer.appendChild(cartElement);
        }
      });
    }

    if (this.loader) {
      this.loader.hide();
    }
    this.initSavedCartItems();
  }

  static updateActiveCartBadge(quantity) {
    const $activeCartItem = MastheadCartDropdownItems.$getActiveCartItem();
    if ($activeCartItem) {
      const $badge = $activeCartItem.querySelector(SELECTORS.SAVED_CART_BADGE);
      if ($badge) {
        MastheadCartDropdownItems.setCartItemBadge($badge, quantity);
      }
    }
  }

  // Inits
  initSavedCartItems() {
    const $cartItems = MastheadCartDropdownItems.$getSavedCartItems();

    $cartItems.forEach($item => {
      $item.addEventListener("click", this.boundHandleCartItemClick);
    });
  }

  initAddCartButton() {
    const $addCartButton = this.$getAddCartButton();
    if ($addCartButton) {
      $addCartButton.addEventListener("click", this.boundHandleAddCartClick);
    }
  }

  init() {
    this.initAddCartButton();

    // Listen for re-initialization events from loader
    document.addEventListener("cart:items:reinit", () => {
      this.initSavedCartItems();
    });
  }
}
